import {DEFAULT_CULTURE_NAME} from "../../core/app/consts";
import DATE_FORMATS from '../../core/app/consts/date-formats';

import {DateFormatDomainModel} from "../../domain/common/datetime/models/date-format-domain-model";
import {datetimeFactory} from "../../domain/common/datetime/domain-services/datetime-factory";
import {DateFormatApiModel} from "../../domain/common/datetime/models/date-format-api-model";
import {BaseUtils} from "./base-utils";
import _isToday from 'date-fns/isToday';
import _parse from 'date-fns/parse';
import _format from 'date-fns/format';
import _addYears from 'date-fns/addYears';
import _addDays from 'date-fns/addDays';
import _addMinutes from 'date-fns/addMinutes';
import _isTomorrow from 'date-fns/isTomorrow';
import _isYesterday from 'date-fns/isYesterday';
import _differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import _differenceInMinutes from 'date-fns/differenceInMinutes';
import _differenceInSeconds from 'date-fns/differenceInSeconds';
import _differenceInMilliseconds from 'date-fns/differenceInMilliseconds';
import _differenceInHours from 'date-fns/differenceInHours';


export class DatetimeUtils extends BaseUtils {
  dateFormats: DateFormatDomainModel;
  locale: Object;

  static injectables = [];

  constructor() {
    super();

    this.dateFormats =  datetimeFactory.createDateFormatDomainObject(DATE_FORMATS[DEFAULT_CULTURE_NAME]);
  }

  initialize(cultureName: String = DEFAULT_CULTURE_NAME): void {
    const dateFormats: DateFormatApiModel = DATE_FORMATS[cultureName];
    const domainDateFormats: DateFormatDomainModel = datetimeFactory.createDateFormatDomainObject(dateFormats);
    this.dateFormats = domainDateFormats;

    this.resolveLocale(domainDateFormats.localeName);
  }

  async resolveLocale(localeName: String) {
    let locale: Object;

    if (localeName) {

      locale = await this.loadLocale(localeName);
      this.locale = locale.default;
    } else {
      this.locale = null;
    }
  }

  fromString(date: String = '', inputFormat: String = 'dd-MM-yyyy hh:mm'): Date {
    const dateObject: Date = _parse(date, inputFormat, new Date());

    return dateObject;
  }

  toISO(date) {
    return date?.toISOString();
  }

  fromISO(isoString: String = new Date().toISOString()): Date {
    const dateObject: Date = new Date(isoString);

    return dateObject;
  }

  _toFormat(date: Date = new Date(), format: String, ignoreLocale: Boolean = false): String {
    const formattedDate = _format(date, format, {locale: this.locale})

    return formattedDate;
  }

  toDateObject(date: DateTime = new Date()): Date {
    const dateObject: Date = new Date(date.valueOf());

    return dateObject;
  }

  toURLDate(date: Date = new Date()): String {
    const result: String = this._toFormat(date, 'yyyy-MM-dd', true);

    return result;
  }

  toShortDate(date: Date = new Date()): String {
    const result: String = this._toFormat(date, this.dateFormats.shortDatePattern);

    return result;
  }

  toShortestDate(date: Date = new Date()): String {
    const result: String = this._toFormat(date, this.dateFormats.shortestDatePattern);

    return result;
  }

  toShortTime(date: Date = new Date()): String {
    const result: String = this._toFormat(date, this.dateFormats.shortTimePattern);

    return result;
  }

  toLongNamedDate(date: Date = new Date()): String {
    const result: String = this._toFormat(date, this.dateFormats.longDateNamedPattern);

    return result;
  }

  toShortNameMonth(date: Date = new Date()): String {
    const result: String = this._toFormat(date, this.dateFormats.shortMonthNamePattern);

    return result;
  }

  toShortNamedDate(date: Date = new Date()): String {
    const result: String = this._toFormat(date, this.dateFormats.desktopPickerPattern);

    return result;
  }

  toShortestNamedDate(date: Date = new Date()): String {
    const result: String = this._toFormat(date, this.dateFormats.shortDesktopPickerPattern);

    return result;
  }

  toShortNameDateTime(date: Date = new Date()): String {
    const result: String = this._toFormat(date, this.dateFormats.desktopDateTimeNamedPattern);

    return result;
  }

  toShortNamedDay(date: Date = new Date()): String {
    const result: String = this._toFormat(date, this.dateFormats.shortDayNamedPattern);

    return result;
  }

  toShortDatetime(date: Date = new Date()): String {
    const result: String = this._toFormat(date, this.dateFormats.shortDatePattern + ' ' + this.dateFormats.shortTimePattern);

    return result;
  }

  toServerDate(date: Date = new Date()): String {
    const result: String = this._toFormat(date, 'dd/MM/yyyy', true);

    return result;
  }

  addYears(datetime: Date = new Date(), amount: Number = 0): Date {
    return _addYears(datetime, amount);
  }

  addDays(datetime: Date = new Date(), amount: Number = 0): Date {
    return _addDays(datetime, amount);
  }

  addMinutes(datetime: Date = new Date(), amount: Number = 0): Date {
    return _addMinutes(datetime, amount);
  }


  isToday(date): Boolean {
    return _isToday(date);
  }

  isTomorrow(date): Boolean {
    return _isTomorrow(date);
  }

  isYesterday(date): Boolean {
    return _isYesterday(date);
  }

  diffDaysByDate(from: Date = new Date(), to: Date = new Date()): Number {
    const diffDays: Number = _differenceInCalendarDays(from, to);

    return diffDays;
  }

  diffSecondsByDate(from: Date = new Date(), to: Date = new Date()): Number {
    const diffSeconds: Number = _differenceInSeconds(from, to);

    return diffSeconds;
  }

  diffMinutesByDate(from: Date = new Date(), to: Date = new Date()): Number {
    const diffMinutes: Number = _differenceInMinutes(from, to);

    return diffMinutes;
  }

  diffHoursByDate(from: Date = new Date(), to: Date = new Date()): Number {
    const diffHours: Number = _differenceInHours(from, to);

    return diffHours;
  }

  diffMillisecondsByDate(from: Date = new Date(), to: Date = new Date()): Number {
    const diffHours: Number = _differenceInMilliseconds(from, to);

    return diffHours;
  }

  diff(from: Date = new Date(), to: Date = new Date()) {
    const diffTime = _differenceInMilliseconds(from, to);

    return diffTime;
  }

  isDateInRange(date: Date, first: Date, last: Date, includeToday: boolean = false): Boolean {
    if (!date || !first || !last) {
      return false;
    }

    const diffDaysFromFirst = this.diffDaysByDate(first, date);
    const diffDaysFromLast = this.diffDaysByDate(date, last);

    if (includeToday) {
      return diffDaysFromFirst <= 0 && diffDaysFromLast <= 0;
    }

    return diffDaysFromFirst < 0 && diffDaysFromLast < 0;
  }

  getRelativeDate = (offsetDays: Number) => {
    const date = new Date();
    date.setDate(date.getDate() + offsetDays);

    return date;
  }

  loadLocale = async (localeName) => {
    const localeMap = {
      'en-US': () => import(/* webpackChunkName: "locale-en-us" */ 'date-fns/locale/en-US'),
      'es': () => import(/* webpackChunkName: "locale-es" */ 'date-fns/locale/es'),
      'ar': () => import(/* webpackChunkName: "locale-ar" */ 'date-fns/locale/ar'),
      'pt-BR': () => import(/* webpackChunkName: "locale-pt-br" */ 'date-fns/locale/pt-BR'),
      'cs': () => import(/* webpackChunkName: "locale-cs" */ 'date-fns/locale/cs'),
      'de': () => import(/* webpackChunkName: "locale-de" */ 'date-fns/locale/de'),
      'en-GB': () => import(/* webpackChunkName: "locale-en-gb" */ 'date-fns/locale/en-GB'),
      'fr': () => import(/* webpackChunkName: "locale-fr" */ 'date-fns/locale/fr'),
      'it': () => import(/* webpackChunkName: "locale-it" */ 'date-fns/locale/it'),
      'hu': () => import(/* webpackChunkName: "locale-hu" */ 'date-fns/locale/hu'),
      'nl': () => import(/* webpackChunkName: "locale-nl" */ 'date-fns/locale/nl'),
      'pt': () => import(/* webpackChunkName: "locale-pt" */ 'date-fns/locale/pt'),
      'ru': () => import(/* webpackChunkName: "locale-ru" */ 'date-fns/locale/ru'),
      'ro': () => import(/* webpackChunkName: "locale-ro" */ 'date-fns/locale/ro'),
      'sk': () => import(/* webpackChunkName: "locale-sk" */ 'date-fns/locale/sk'),
      'sr-Latn': () => import(/* webpackChunkName: "locale-sr-latn" */ 'date-fns/locale/sr-Latn'),
      'tr': () => import(/* webpackChunkName: "locale-tr" */ 'date-fns/locale/tr'),
      'el': () => import(/* webpackChunkName: "locale-el" */ 'date-fns/locale/el'),
      'bg': () => import(/* webpackChunkName: "locale-bg" */ 'date-fns/locale/bg'),
      'uk': () => import(/* webpackChunkName: "locale-uk" */ 'date-fns/locale/uk'),
      'th': () => import(/* webpackChunkName: "locale-th" */ 'date-fns/locale/th'),
      'zh-CN': () => import(/* webpackChunkName: "locale-zh-cn" */ 'date-fns/locale/zh-CN'),
      'ja': () => import(/* webpackChunkName: "locale-ja" */ 'date-fns/locale/ja'),
      'de-AT': () => import(/* webpackChunkName: "locale-de-at" */ 'date-fns/locale/de-AT'),
      'nl-BE': () => import(/* webpackChunkName: "locale-nl-be" */ 'date-fns/locale/nl-BE'),
      'he': () => import(/* webpackChunkName: "locale-he" */ 'date-fns/locale/he'),
      'id': () => import(/* webpackChunkName: "locale-id" */ 'date-fns/locale/id'),
      'pl': () => import(/* webpackChunkName: "locale-pl" */ 'date-fns/locale/pl'),
      'da': () => import(/* webpackChunkName: "locale-da" */ 'date-fns/locale/da'),
      'fi': () => import(/* webpackChunkName: "locale-fi" */ 'date-fns/locale/fi'),
      'nn': () => import(/* webpackChunkName: "locale-nn" */ 'date-fns/locale/nn'),
      'bs': () => import(/* webpackChunkName: "locale-bs" */ 'date-fns/locale/bs'),
      'sv': () => import(/* webpackChunkName: "locale-sv" */ 'date-fns/locale/sv'),
      'zh-TW': () => import(/* webpackChunkName: "locale-zh-tw" */ 'date-fns/locale/zh-TW'),
      'hi': () => import(/* webpackChunkName: "locale-hi" */ 'date-fns/locale/hi'),
      'vi': () => import(/* webpackChunkName: "locale-vi" */ 'date-fns/locale/vi'),
      'fa-IR': () => import(/* webpackChunkName: "locale-fa-ir" */ 'date-fns/locale/fa-IR')
    };
    if (!localeMap[localeName]) {
      throw new Error(`Locale '${localeName}' not supported.`);
    }

    return localeMap[localeName]();
  }
}
